import React, { useCallback, useEffect, useState } from 'react';
import { gameApi, GameSummary, UserRole } from '../api';

interface AdminGamesViewProps {
  playerName: string;
  authRole: UserRole | null;
  isAuthenticated: boolean;
  onBack: () => void;
  onSelectGame: (gameId: string) => void;
}

const formatUserId = (id?: string | null) => {
  if (!id) return '';
  return id.length > 8 ? `${id.slice(0, 8)}...` : id;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

export const AdminGamesView: React.FC<AdminGamesViewProps> = ({
  playerName,
  authRole,
  isAuthenticated,
  onBack,
  onSelectGame,
}) => {
  const [adminGames, setAdminGames] = useState<GameSummary[]>([]);
  const [gameListType, setGameListType] = useState<'unfinished' | 'finished'>('unfinished');
  const [adminGamesLoading, setAdminGamesLoading] = useState<boolean>(false);
  const [adminGamesError, setAdminGamesError] = useState<string | null>(null);
  const [ownerFilterInput, setOwnerFilterInput] = useState('');
  const [appliedOwnerFilter, setAppliedOwnerFilter] = useState('');
  const [finishedPage, setFinishedPage] = useState(1);
  const [finishedTotalCount, setFinishedTotalCount] = useState(0);
  const [finishedTotalPages, setFinishedTotalPages] = useState(0);

  const fetchAdminGames = useCallback(async () => {
    if (!isAuthenticated) {
      setAdminGamesError('Login required');
      return;
    }
    if (authRole !== 'Admin') {
      setAdminGamesError('Admin access required');
      return;
    }
    setAdminGamesLoading(true);
    setAdminGamesError(null);
    try {
      if (gameListType === 'finished') {
        const response = await gameApi.listAllFinishedGames(
          finishedPage,
          appliedOwnerFilter || undefined
        );
        setAdminGames(response.games);
        setFinishedTotalCount(response.totalCount);
        setFinishedTotalPages(response.totalPages);
      } else {
        const games = await gameApi.listAllGames();
        setAdminGames(games);
      }
    } catch (err) {
      setAdminGamesError(err instanceof Error ? err.message : 'Failed to load admin games');
    } finally {
      setAdminGamesLoading(false);
    }
  }, [appliedOwnerFilter, authRole, finishedPage, gameListType, isAuthenticated]);

  const handleApplyOwnerFilter = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedFilter = ownerFilterInput.trim();
    if (finishedPage === 1 && appliedOwnerFilter === normalizedFilter) {
      void fetchAdminGames();
      return;
    }

    setFinishedPage(1);
    setAppliedOwnerFilter(normalizedFilter);
  }, [appliedOwnerFilter, fetchAdminGames, finishedPage, ownerFilterInput]);

  const handleClearOwnerFilter = useCallback(() => {
    setOwnerFilterInput('');
    if (finishedPage === 1 && appliedOwnerFilter === '') {
      void fetchAdminGames();
      return;
    }

    setFinishedPage(1);
    setAppliedOwnerFilter('');
  }, [appliedOwnerFilter, fetchAdminGames, finishedPage]);

  const handleDeleteGame = useCallback(async (gameId: string) => {
    if (!isAuthenticated) {
      setAdminGamesError('Login is required to delete game');
      return;
    }
    if (!gameId) return;

    setAdminGamesError(null);
    setAdminGamesLoading(true);
    try {
      await gameApi.adminDeleteGame(gameId);
      if (gameListType === 'finished') {
        if (adminGames.length === 1 && finishedPage > 1) {
          setFinishedPage(previousPage => previousPage - 1);
        } else {
          await fetchAdminGames();
        }
      } else {
        setAdminGames(prev => prev.filter(g => g.gameId !== gameId));
      }
    } catch (err) {
      setAdminGamesError(err instanceof Error ? err.message : 'Failed to delete game');
    } finally {
      setAdminGamesLoading(false);
    }
  }, [adminGames.length, fetchAdminGames, finishedPage, gameListType, isAuthenticated]);

  useEffect(() => {
    if (authRole === 'Admin') {
      fetchAdminGames();
    }
  }, [authRole, fetchAdminGames]);

  if (authRole !== 'Admin') {
    return (
      <div className="home-menu">
        <h1>Admin Games</h1>
        <div className="error">Admin access required.</div>
        <button onClick={onBack}>Back Home</button>
      </div>
    );
  }

  return (
    <div className="home-menu">
      <div className="player-name-section-home">
        <p className="player-name-home">Player: {playerName}</p>
        <button onClick={onBack} className="change-name-btn">
          Back Home
        </button>
      </div>
      <h1>Admin Games</h1>
      <div className="games-table">
        <div className="admin-actions">
          <button
            onClick={() => {
              setGameListType('unfinished');
              setFinishedPage(1);
            }}
            disabled={adminGamesLoading || gameListType === 'unfinished'}
          >
            Unfinished
          </button>
          <button
            onClick={() => {
              setGameListType('finished');
              setFinishedPage(1);
            }}
            disabled={adminGamesLoading || gameListType === 'finished'}
          >
            Finished
          </button>
          <button onClick={fetchAdminGames} disabled={adminGamesLoading}>
            Refresh
          </button>
        </div>
        {gameListType === 'finished' && (
          <form className="admin-games-filter" onSubmit={handleApplyOwnerFilter}>
            <label htmlFor="finished-games-owner-filter">Owner</label>
            <input
              id="finished-games-owner-filter"
              value={ownerFilterInput}
              onChange={event => setOwnerFilterInput(event.target.value)}
              placeholder="Filter by owner"
              disabled={adminGamesLoading}
            />
            <button type="submit" disabled={adminGamesLoading}>Filter</button>
            <button
              type="button"
              onClick={handleClearOwnerFilter}
              disabled={adminGamesLoading || (!ownerFilterInput && !appliedOwnerFilter)}
            >
              Clear
            </button>
          </form>
        )}
        {adminGamesError && <div className="error">{adminGamesError}</div>}
        {adminGamesLoading && <div>Loading games...</div>}
        {!adminGamesLoading && adminGames.length === 0 && (
          <div>{gameListType === 'finished' ? 'No finished games available.' : 'No games available.'}</div>
        )}
        {!adminGamesLoading && adminGames.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Game</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Players</th>
                <th>First</th>
                <th>Created</th>
                <th>Last Action</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {adminGames.map(g => {
                const currentName = playerName?.trim().toLowerCase();
                const isOwnerByName =
                  g.owner &&
                  currentName &&
                  g.owner.toLowerCase() === currentName;
                const ownerDisplayId = g.ownerUserId ? formatUserId(g.ownerUserId) : '';
                const ownerLabelBase = g.owner ?? (ownerDisplayId ? `User ${ownerDisplayId}` : '-');
                const ownerLabel = isOwnerByName ? `${ownerLabelBase} (you)` : ownerLabelBase;

                return (
                  <tr key={g.gameId}>
                    <td>{g.gameId.slice(0, 8)}...</td>
                    <td>{ownerLabel}</td>
                    <td>{g.status}</td>
                    <td>
                      {g.playersCount}/{g.expectedPlayersCount}
                      {g.players.length > 0 && (
                        <div className="player-list">
                          {g.players.map(name => (
                            <span key={name}>
                              {name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>{g.firstPlayer || '-'}</td>
                    <td>{formatDateTime(g.createdAt)}</td>
                    <td>{formatDateTime(g.lastActionAt ?? g.lastUpdatedAt ?? null)}</td>
                    <td className="game-actions">
                      <button onClick={() => onSelectGame(g.gameId)}>Open</button>
                      <button
                        className="delete-button"
                        onClick={() => handleDeleteGame(g.gameId)}
                        disabled={adminGamesLoading}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!adminGamesLoading && gameListType === 'finished' && finishedTotalCount > 0 && (
          <div className="admin-games-pagination">
            <button
              onClick={() => setFinishedPage(currentPage => currentPage - 1)}
              disabled={finishedPage <= 1}
            >
              Previous
            </button>
            <span>
              Page {finishedPage} of {finishedTotalPages} ({finishedTotalCount} games)
            </span>
            <button
              onClick={() => setFinishedPage(currentPage => currentPage + 1)}
              disabled={finishedPage >= finishedTotalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
